import { Order } from '@api/Orders/Order';

// ============================================================================
// La ventana de pedidos del tablero, COMPARTIDA entre montajes.
//
// ## El problema, medido
// El tablero de `/orders` escucha una ventana de fechas con `onSnapshot`, y la
// **primera entrega de un listener trae la ventana completa** — ~180 pedidos en
// un día normal. El listener se creaba en el `useEffect` de la pantalla, así que
// se cobraba esa ventana entera **en cada montaje**: y el trabajo real del día
// es ir y venir entre el POS y el tablero decenas de veces. Encima, el selector
// de fechas hace `setFilters({ ...filters, startDate })` y luego otro para
// `endDate`: dos objetos nuevos → el efecto, que dependía del OBJETO, rearmaba
// los listeners **dos veces** por cada cambio de rango.
//
// ## La forma de la solución
// Una suscripción por ventana, con **cuenta de suscriptores** y un **periodo de
// gracia** al quedarse sin ninguno:
//
// - el primero en pedir una ventana crea el listener de verdad;
// - **el que llega después recibe el último snapshot en el acto y NO cuesta
//   nada** — que es justo el viaje POS → tablero;
// - cuando el último se va, el listener NO se cierra: se agenda su cierre. Si
//   alguien vuelve dentro de la gracia (volver del POS), **se cancela el cierre
//   y no se relee nada**.
//
// El periodo de gracia es el precio consciente de esto: durante esos segundos un
// listener sin pantalla sigue cobrando las ENTREGAS (no la ventana completa, que
// ya se pagó). A cambio se ahorra una ventana entera por cada ida y vuelta. Con
// ~180 pedidos por ventana, la cuenta favorece a la gracia desde el primer
// regreso.
//
// ⚠️ Lo que esto NO hace: no cachea entre recargas de la pestaña (es memoria del
// módulo) ni cambia lo que el listener consulta. Es puro manejo del ciclo de
// vida — por eso se puede probar sin Firestore, inyectando el `factory`.
// ============================================================================

export interface OrdersWindowKey {
    startMillis: number;
    endMillis: number;
    branchOfficeId: string;
}

export type OrdersListener = (orders: Order[]) => void;

/**
 * Quien de verdad crea el `onSnapshot`. Se inyecta para poder probar esto sin
 * Firestore: recibe la ventana y un `emit`, y devuelve su `unsubscribe`.
 */
export type OrdersWindowFactory = (key: OrdersWindowKey, emit: OrdersListener) => () => void;

/**
 * Cuánto sobrevive un listener sin pantalla. 90 s cubre de sobra un viaje al POS
 * para cobrar; más allá de eso, quien vuelve probablemente ya cambió de contexto
 * y pagar la ventana otra vez es lo correcto.
 */
export const DEFAULT_GRACE_MS = 90_000;

interface Entry {
    key: OrdersWindowKey;
    listeners: Set<OrdersListener>;
    /** El último snapshot entregado: lo que recibe en el acto quien llega después. */
    lastOrders?: Order[];
    stop: () => void;
    closeTimer?: ReturnType<typeof setTimeout>;
}

const entries = new Map<string, Entry>();

const keyOf = (key: OrdersWindowKey): string =>
    `${key.branchOfficeId}|${key.startMillis}|${key.endMillis}`;

/**
 * Se suscribe a una ventana de pedidos. Devuelve la baja.
 *
 * Si ya hay una suscripción viva para esa MISMA ventana, se engancha a ella: el
 * `listener` recibe el último snapshot de inmediato y no se crea nada.
 */
export const subscribeOrdersWindow = (
    key: OrdersWindowKey,
    factory: OrdersWindowFactory,
    listener: OrdersListener,
    options?: { graceMs?: number }
): () => void => {
    const graceMs = options?.graceMs ?? DEFAULT_GRACE_MS;
    const id = keyOf(key);
    let entry = entries.get(id);

    if (entry) {
        // Volvió alguien: si había cierre agendado, se cancela — y aquí es donde
        // se ahorra la ventana completa.
        if (entry.closeTimer) {
            clearTimeout(entry.closeTimer);
            entry.closeTimer = undefined;
        }
        entry.listeners.add(listener);
        if (entry.lastOrders) listener(entry.lastOrders);
    } else {
        const created: Entry = { key, listeners: new Set([listener]), stop: () => {} };
        entries.set(id, created);
        // El `emit` se resuelve contra `entries` y no contra `created` para que un
        // snapshot que llegue después del cierre real no reviva nada.
        created.stop = factory(key, (orders) => {
            const current = entries.get(id);
            if (!current) return;
            current.lastOrders = orders;
            current.listeners.forEach((fn) => fn(orders));
        });
        entry = created;
    }

    let active = true;
    return () => {
        // Idempotente: React puede llamar el cleanup más de una vez y una baja
        // repetida no debe descontar de más ni cerrar la ventana de otro.
        if (!active) return;
        active = false;
        const current = entries.get(id);
        if (!current) return;
        current.listeners.delete(listener);
        if (current.listeners.size > 0) return;
        current.closeTimer = setTimeout(() => {
            const stillThere = entries.get(id);
            if (!stillThere || stillThere.listeners.size > 0) return;
            entries.delete(id);
            stillThere.stop();
        }, graceMs);
    };
};

/** Cuántos listeners de Firestore hay vivos ahora mismo. Para pruebas y diagnóstico. */
export const getActiveOrdersWindows = (): number => entries.size;

/** Cierra todo YA, sin gracia. Solo para pruebas y para un logout. */
export const resetOrdersWindows = (): void => {
    entries.forEach((entry) => {
        if (entry.closeTimer) clearTimeout(entry.closeTimer);
        entry.stop();
    });
    entries.clear();
};
